import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { onDashboardChanged } from "@/lib/dashboardWs";

/**
 * NATS — a live view into what's actually sitting in JetStream (every
 * stream's message/byte counts and per-consumer lag) and a bounded
 * sample of core pub/sub subjects, direct operator request 2026-09-17
 * after a real live incident this same day spent a long time guessing
 * whether a fix had actually reached NATS at all, with no way to just
 * look.
 *
 * Architecture note, because it shapes what this page can and can't
 * show: no Cloudflare Worker can talk to NATS directly anywhere on this
 * platform. Every number here is relayed through a real box's own
 * deploy-agent (POST /v1/fleet/diagnose {box_id, op, args} — the exact
 * same relay FleetPage.tsx's DiagnosePanel already uses for
 * diagnose-unit), which is why a box picker comes first: JetStream
 * stream/consumer state is genuinely cluster-wide (any connected box
 * sees the same answer), but the "live tail" of a core subject only
 * shows what THAT box itself is subscribed to — see internal/deploy's
 * actions.ts (nats-streams/nats-stream-detail/nats-stream-peek/nats-tail)
 * for the full read-only op definitions this page calls, including
 * nats-tail's own deliberate restriction to subjects the target box's
 * NATS identity already has a real subscribe grant for.
 */

interface FleetBoxLite {
  box_id: string;
  hostname: string;
  connected: boolean;
  state: "up" | "degraded" | "down" | "unknown";
  /** "macos" boxes are not offered: the macOS fleet host has no NATS at all. */
  kind?: "linux" | "macos" | null;
}

interface StreamSummary {
  name: string;
  subjects: string[];
  retention: string;
  messages: number;
  bytes: number;
  first_seq: number;
  last_seq: number;
  consumer_count: number;
}

interface ConsumerSummary {
  name: string;
  filter_subject: string;
  num_pending: number;
  num_ack_pending: number;
  num_redelivered: number;
  num_waiting: number;
  delivered_stream_seq: number;
  ack_floor_stream_seq: number;
}

interface StreamDetail extends StreamSummary {
  consumers: ConsumerSummary[];
}

interface PeekMessage {
  seq: number;
  subject: string;
  time: string;
  size_bytes: number;
  data_preview: string;
  truncated: boolean;
}

interface TailMessage {
  subject: string;
  time: string;
  data_preview: string;
  truncated: boolean;
}

/** Mirrors internal/deploy's actions.ts TAILABLE_SUBJECTS exactly — kept
 *  in sync by hand (same convention as several other duplicated-by-hand
 *  lists across this platform). Only used to populate the dropdown;
 *  nats-tail validates for real on the agent side regardless, so a
 *  drifted copy here fails safely (a 400 from the relay, not a security
 *  gap). */
const TAILABLE_SUBJECTS = [
  "pure.platform.upgrade.>",
  "pure.runner.status.>",
  "pure.nats-cluster.status.>",
  "pure.publish.status.>",
  "pure.publish.pipeline",
];

function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtTime(iso: string): string {
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? iso : t.toLocaleTimeString();
}

/** Best-effort pretty-print — most subjects on this platform carry JSON,
 *  but this is a raw debugging view, so a non-JSON payload just renders
 *  as-is rather than erroring. */
function prettyPreview(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function NatsPage() {
  const token = useAuthToken();
  const call = useCallback(
    <T,>(path: string, init?: RequestInit) => api<T>(token, path, init),
    [token],
  );
  const diagnose = useCallback(
    <T,>(boxId: string, op: string, args?: Record<string, unknown>) =>
      call<{ ok: boolean; payload: T; reason: string | null }>(
        "/fleet/diagnose",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ box_id: boxId, op, args: args ?? {} }),
        },
      ),
    [call],
  );

  const [boxes, setBoxes] = useState<FleetBoxLite[] | null>(null);
  const [boxesError, setBoxesError] = useState<string | null>(null);
  const [boxId, setBoxId] = useState("");

  const loadBoxes = useCallback(() => {
    call<{ ok: true; boxes: FleetBoxLite[] }>("/fleet/overview")
      .then((r) => {
        // The macOS fleet host is deliberately absent: it has no deploy-agent, its agent
        // channel refuses every nats-* op, and its own NATS grant is only its job/result
        // subjects (natsJwtMint.ts).
        const natsBoxes = r.boxes.filter((b) => b.kind !== "macos");
        setBoxes(natsBoxes);
        setBoxesError(null);
        // Default to the first CONNECTED box — a disconnected one can't
        // relay anything, so picking it by default would just show an
        // immediate, confusing error.
        setBoxId((prev) =>
          prev && natsBoxes.some((b) => b.box_id === prev)
            ? prev
            : (natsBoxes.find((b) => b.connected)?.box_id ??
              natsBoxes[0]?.box_id ??
              ""),
        );
      })
      .catch((err) => setBoxesError(errMsg(err, "failed to load fleet boxes")));
  }, [call]);
  useEffect(loadBoxes, [loadBoxes]);
  useEffect(() => onDashboardChanged(token, loadBoxes), [token, loadBoxes]);

  const selectedBox = useMemo(
    () => boxes?.find((b) => b.box_id === boxId) ?? null,
    [boxes, boxId],
  );

  // ── Streams ──────────────────────────────────────────────────────────
  const [streams, setStreams] = useState<StreamSummary[] | null>(null);
  const [streamsError, setStreamsError] = useState<string | null>(null);
  const [streamsLoading, setStreamsLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<StreamDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [peek, setPeek] = useState<PeekMessage[] | null>(null);
  const [peekError, setPeekError] = useState<string | null>(null);
  const [peekLoading, setPeekLoading] = useState(false);

  const loadStreams = useCallback(() => {
    if (!boxId) return;
    setStreamsLoading(true);
    diagnose<{ streams: StreamSummary[] }>(boxId, "nats-streams")
      .then((r) => {
        if (r.ok) {
          setStreams(r.payload.streams);
          setStreamsError(null);
        } else {
          setStreams(null);
          setStreamsError(r.reason ?? "failed to list streams");
        }
      })
      .catch((err) => setStreamsError(errMsg(err, "failed to list streams")))
      .finally(() => setStreamsLoading(false));
  }, [diagnose, boxId]);
  useEffect(() => {
    setExpanded(null);
    setDetail(null);
    setPeek(null);
    loadStreams();
  }, [loadStreams]);

  function toggleStream(name: string) {
    if (expanded === name) {
      setExpanded(null);
      return;
    }
    setExpanded(name);
    setDetail(null);
    setDetailError(null);
    setPeek(null);
    setPeekError(null);
    setDetailLoading(true);
    diagnose<StreamDetail>(boxId, "nats-stream-detail", { stream: name })
      .then((r) => {
        if (r.ok) setDetail(r.payload);
        else setDetailError(r.reason ?? "failed to load stream detail");
      })
      .catch((err) =>
        setDetailError(errMsg(err, "failed to load stream detail")),
      )
      .finally(() => setDetailLoading(false));
  }

  function runPeek(name: string) {
    setPeekLoading(true);
    setPeekError(null);
    diagnose<{ messages: PeekMessage[] }>(boxId, "nats-stream-peek", {
      stream: name,
      count: 10,
    })
      .then((r) => {
        if (r.ok) setPeek(r.payload.messages);
        else setPeekError(r.reason ?? "failed to peek stream");
      })
      .catch((err) => setPeekError(errMsg(err, "failed to peek stream")))
      .finally(() => setPeekLoading(false));
  }

  // ── Live tail ────────────────────────────────────────────────────────
  const [tailSubject, setTailSubject] = useState(TAILABLE_SUBJECTS[0]!);
  const [tailState, setTailState] = useState<
    | { kind: "idle" }
    | { kind: "capturing" }
    | { kind: "error"; message: string }
    | { kind: "done"; messages: TailMessage[] }
  >({ kind: "idle" });

  function runTail() {
    setTailState({ kind: "capturing" });
    diagnose<{ captured: number; messages: TailMessage[] }>(
      boxId,
      "nats-tail",
      { subject: tailSubject, timeout_ms: 6_000, max_messages: 50 },
    )
      .then((r) => {
        if (r.ok) setTailState({ kind: "done", messages: r.payload.messages });
        else
          setTailState({
            kind: "error",
            message: r.reason ?? "tail failed",
          });
      })
      .catch((err) =>
        setTailState({ kind: "error", message: errMsg(err, "tail failed") }),
      );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">NATS</h1>
        <p className="text-sm text-muted-foreground">
          Live JetStream stream/consumer state and a bounded sample of core
          subjects — relayed through one box's own deploy-agent, since nothing
          in this dashboard can talk to NATS directly. JetStream state is
          cluster-wide (any connected box sees the same answer); the live tail
          below only shows what the selected box itself can see. The macOS fleet
          host is not listed: it talks to the broker over HTTPS and its agent
          WebSocket only and has no NATS access by design.
        </p>
      </div>

      {boxesError && <p className="text-sm text-destructive">{boxesError}</p>}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <Label htmlFor="nats-box" className="text-sm">
              Relay through
            </Label>
            <Select
              id="nats-box"
              className="w-72"
              value={boxId}
              onChange={(e) => setBoxId(e.target.value)}
            >
              {boxes?.map((b) => (
                <option key={b.box_id} value={b.box_id} disabled={!b.connected}>
                  {b.hostname}
                  {b.connected ? "" : " (disconnected)"}
                </option>
              ))}
            </Select>
            {selectedBox && (
              <Badge variant={selectedBox.connected ? "success" : "secondary"}>
                {selectedBox.connected ? "connected" : "disconnected"}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={loadStreams}
              disabled={!boxId}
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Streams</CardTitle>
          <CardDescription>
            Every JetStream stream this box's own NATS connection can see —
            message/byte counts are the stream's CURRENT state (what's actually
            retained right now), not a historical total. Click a row for its
            consumers and to peek at recent messages.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {streamsError && (
            <p className="text-sm text-destructive mb-2">{streamsError}</p>
          )}
          {streamsLoading && !streams && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {streams && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-6" />
                  <TableHead>Stream</TableHead>
                  <TableHead>Subjects</TableHead>
                  <TableHead>Retention</TableHead>
                  <TableHead>Messages</TableHead>
                  <TableHead>Bytes</TableHead>
                  <TableHead>Consumers</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {streams.map((s) => (
                  <Fragment key={s.name}>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleStream(s.name)}
                    >
                      <TableCell className="text-muted-foreground">
                        {expanded === s.name ? "▾" : "▸"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {s.name}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {s.subjects.join(", ")}
                      </TableCell>
                      <TableCell className="text-xs">{s.retention}</TableCell>
                      <TableCell>
                        <Badge
                          variant={s.messages > 0 ? "default" : "secondary"}
                        >
                          {s.messages}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {fmtBytes(s.bytes)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {s.consumer_count}
                      </TableCell>
                    </TableRow>
                    {expanded === s.name && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          colSpan={7}
                          className="bg-muted/30 p-4 space-y-4"
                        >
                          {detailLoading && (
                            <p className="text-sm text-muted-foreground">
                              Loading…
                            </p>
                          )}
                          {detailError && (
                            <p className="text-sm text-destructive">
                              {detailError}
                            </p>
                          )}
                          {detail && detail.name === s.name && (
                            <div>
                              <div className="text-xs font-semibold text-muted-foreground mb-2">
                                Consumers
                              </div>
                              {detail.consumers.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                  No consumers on this stream.
                                </p>
                              ) : (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Consumer</TableHead>
                                      <TableHead>Filter</TableHead>
                                      <TableHead>Pending</TableHead>
                                      <TableHead>Ack pending</TableHead>
                                      <TableHead>Redelivered</TableHead>
                                      <TableHead>Lag (stream seq)</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {detail.consumers.map((c) => {
                                      const lag =
                                        detail.last_seq -
                                        c.delivered_stream_seq;
                                      return (
                                        <TableRow key={c.name}>
                                          <TableCell className="font-mono text-xs">
                                            {c.name}
                                          </TableCell>
                                          <TableCell className="font-mono text-xs text-muted-foreground">
                                            {c.filter_subject || "(all)"}
                                          </TableCell>
                                          <TableCell>
                                            <Badge
                                              variant={
                                                c.num_pending > 0
                                                  ? "warning"
                                                  : "secondary"
                                              }
                                            >
                                              {c.num_pending}
                                            </Badge>
                                          </TableCell>
                                          <TableCell>
                                            <Badge
                                              variant={
                                                c.num_ack_pending > 0
                                                  ? "warning"
                                                  : "secondary"
                                              }
                                            >
                                              {c.num_ack_pending}
                                            </Badge>
                                          </TableCell>
                                          <TableCell>
                                            <Badge
                                              variant={
                                                c.num_redelivered > 0
                                                  ? "destructive"
                                                  : "secondary"
                                              }
                                            >
                                              {c.num_redelivered}
                                            </Badge>
                                          </TableCell>
                                          <TableCell className="text-xs text-muted-foreground">
                                            {lag > 0
                                              ? `${lag} behind`
                                              : "caught up"}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </div>
                          )}

                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-semibold text-muted-foreground">
                                Recent messages
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => runPeek(s.name)}
                                disabled={peekLoading}
                              >
                                {peekLoading ? "Peeking…" : "Peek last 10"}
                              </Button>
                            </div>
                            {peekError && (
                              <p className="text-sm text-destructive">
                                {peekError}
                              </p>
                            )}
                            {peek && peek.length === 0 && (
                              <p className="text-sm text-muted-foreground">
                                Nothing currently in this stream (workqueue
                                streams only retain unacknowledged messages —
                                this can be completely normal).
                              </p>
                            )}
                            {peek && peek.length > 0 && (
                              <div className="space-y-2">
                                {peek.map((m) => (
                                  <div
                                    key={m.seq}
                                    className="rounded-md border bg-background p-2"
                                  >
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-1">
                                      <span className="font-mono">
                                        seq {m.seq}
                                      </span>
                                      <span className="font-mono">
                                        {m.subject}
                                      </span>
                                      <span>{fmtTime(m.time)}</span>
                                      <span>{fmtBytes(m.size_bytes)}</span>
                                      {m.truncated && (
                                        <Badge variant="warning">
                                          truncated
                                        </Badge>
                                      )}
                                    </div>
                                    <pre className="text-xs font-mono whitespace-pre-wrap break-words overflow-x-auto">
                                      {prettyPreview(m.data_preview)}
                                    </pre>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
          {streams?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No JetStream streams visible from this box.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Live tail</CardTitle>
          <CardDescription>
            Samples a core (non-JetStream) subject for a few seconds — these are
            plain pub/sub, so there's nothing to query after the fact, only what
            flows by while capturing. Limited to subjects this box's own NATS
            identity is already granted to see.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              className="w-72"
              value={tailSubject}
              onChange={(e) => setTailSubject(e.target.value)}
            >
              {TAILABLE_SUBJECTS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
            <Button
              onClick={runTail}
              disabled={!boxId || tailState.kind === "capturing"}
            >
              {tailState.kind === "capturing" ? "Capturing (6s)…" : "Capture"}
            </Button>
          </div>
          {tailState.kind === "error" && (
            <p className="text-sm text-destructive">{tailState.message}</p>
          )}
          {tailState.kind === "done" && tailState.messages.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No messages observed on {tailSubject} in the capture window — that
              can be completely normal for a quiet subject; try again or trigger
              the activity you're expecting to see.
            </p>
          )}
          {tailState.kind === "done" && tailState.messages.length > 0 && (
            <div className="space-y-2">
              {tailState.messages.map((m, i) => (
                <div key={i} className="rounded-md border bg-background p-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-1">
                    <span className="font-mono">{m.subject}</span>
                    <span>{fmtTime(m.time)}</span>
                    {m.truncated && <Badge variant="warning">truncated</Badge>}
                  </div>
                  <pre className="text-xs font-mono whitespace-pre-wrap break-words overflow-x-auto">
                    {prettyPreview(m.data_preview)}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
